import { useAuth } from "@/src/contexts/auth.context";
import { Post } from "@/src/models/post.model";
import apiClient from "@/src/services/api/axios";
import gqlClient from "@/src/services/api/graphql.client";
import {
  deleteEmbeddedPost,
  embedPost,
  updateEmbeddedPost,
  useCreatePostMutation,
  useDeletePostMutation,
  useGetPostsQuery,
  useUpdatePostMutation,
} from "@/src/services/api/posts/posts.hooks";
import { GET_POSTS_QUERY } from "@/src/services/api/posts/posts.query";
import { hot } from "@/src/utils/feed-score";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { VisibilityType } from "./use-post-creation-modal";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";

let _latchedPostId: string | null = null;


const extractHashtags = (query: string): string[] | null => {
  const tokens = query.trim().split(/\s+/);
  const allHashtags = tokens.every((t) => t.startsWith("#"));
  if (!allHashtags) return null;
  return tokens.map((t) => t.slice(1).toLowerCase()).filter(Boolean);
};

export const usePosts = (insideGroup: Boolean, groupId?: string) => {
  const { user, userGroups } = useAuth();
  const router = useRouter();

  const { search: searchQueryFromUrl, post_id } = useLocalSearchParams<{
    search?: string;
    post_id?: string;
  }>();

  // Safely resolve string from url param — useLocalSearchParams can return string[]
  const initialSearch = Array.isArray(searchQueryFromUrl)
    ? (searchQueryFromUrl[0] ?? "")
    : (searchQueryFromUrl ?? "");

  const [modalOpen, setModalOpen] = useState(false);
  const [editPost, setEditPost] = useState<Post | null>(null);

  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      if (search.trim()) {
        router.setParams({ search: search.trim(), post_id });
      } else {
        router.setParams({ search: undefined, post_id });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const isSearching = debouncedSearch.trim().length > 0;

  if (post_id) {
    _latchedPostId = Array.isArray(post_id) ? (post_id[0] ?? null) : post_id;
  }

  const [viewerPostId, setViewerPostId] = useState<string | null>(
    _latchedPostId,
  );
  const [viewerOpen, setViewerOpen] = useState(!!_latchedPostId);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (_latchedPostId && !dismissed) {
      setViewerPostId(_latchedPostId);
      setViewerOpen(true);
    }
  }, []);

  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [searchResults, setSearchResults] = useState<Post[]>([]);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Reactor modal
  const [showReactorModal, setReactorModalVisibility] = useState(false);

  // Visibility filter
  const filterPostVisibility = (post: Post) => {
    switch (post.visibility) {
      case "public":
        return true;
      case "private":
        return post.CreatedBy === user?.itemId;
      case "specific":
        return post.visibleToUserIds?.includes(user?.itemId ?? "");
      case "group":
        return post.visibleToGroupIds?.some((groupId: string) =>
          userGroups?.includes(groupId),
        );
      default:
        return false;
    }
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useGetPostsQuery(insideGroup ? groupId : undefined);
  const posts = data?.pages.flatMap((page) => page?.getposts?.items ?? []) ?? [];
  const visiblePosts = posts.filter(filterPostVisibility);
  const displayedPosts = hasSearched ? searchResults : visiblePosts;

  const handleSearch = async (page = 1) => {
    const pageNum = typeof page === "number" ? page : 1;
    if (!search.trim()) {
      setSearchResults([]);
      setSearchPage(1);
      setHasSearched(false);
      router.setParams({ search: undefined });
      return;
    }
    if (isSearchLoading) return;
    setIsSearchLoading(true);
    setHasSearched(true);
    router.setParams({ search });
    try {
      const hashtags = extractHashtags(search);

      if (hashtags) {
        const client = await gqlClient();
        const result = await client.request(GET_POSTS_QUERY, {
          filter: JSON.stringify({ hashtags: { $in: hashtags } }),
          sort: JSON.stringify({ CreatedDate: -1 }),
          pageNo: pageNum,
          pageSize: 10,
        });
        const newPosts = (result.getposts?.items ?? []).filter(filterPostVisibility);
        setSearchResults((prev) => pageNum === 1 ? newPosts : [...prev, ...newPosts]);
        setHasMoreSearchResults(result.getposts?.hasNextPage ?? false);
        setSearchPage(result.getposts?.pageNo ?? pageNum);
      } else {
        const res = await apiClient.get(
          `${process.env.EXPO_PUBLIC_EMBEDDING_SERVICE_URL}/posts/search`,
          { params: { query: search, page: pageNum, per_page: 10 } },
        );

        const newIds: string[] = res.data.post_ids;
        if (newIds.length > 0) {
          const client = await gqlClient();
          const result = await client.request(GET_POSTS_QUERY, {
            filter: JSON.stringify({ ItemId: { $in: newIds } }),
            sort: "{}",
            pageNo: 1,
            pageSize: newIds.length,
          });

          const newPosts = newIds
            .map((id) => result.getposts?.items?.find((p: Post) => p.ItemId === id))
            .filter(Boolean)
            .filter(filterPostVisibility);

          setSearchResults((prev) => pageNum === 1 ? newPosts : [...prev, ...newPosts]);
        } else {
          if (pageNum === 1) setSearchResults([]);
        }

        setHasMoreSearchResults(pageNum < res.data.meta.total_pages);
        setSearchPage(pageNum);
      }
    } catch (e) {
      console.error("Search failed:", e);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleLoadMoreSearchResults = () => {
    if (hasMoreSearchResults && !isSearchLoading) {
      handleSearch(searchPage + 1);
    }
  };

  // Mutations
  const createPost = useCreatePostMutation(groupId ?? undefined);
  const updatePost = useUpdatePostMutation(groupId ?? undefined);
  const deletePost = useDeletePostMutation(groupId ?? undefined);

  const handleSubmit = (
    content: string,
    hashtags: string[],
    mentionedUsers: SelectableItem[],
    visibility: VisibilityType,
    attachmentUrls: string[],
    visibleToGroupIds?: string[],
    visibleToUserIds?: string[],
    groupId?: string,
  ) => {
    if (editPost) {
      updatePost.mutate(
        { id: editPost.ItemId, content, hashtags, mentionedUsers, visibility, attachmentUrls, visibleToGroupIds, visibleToUserIds },
        {
          onSuccess: async (data) => {
            try {
              await updateEmbeddedPost({
                post_id: data.updatepost.itemId,
                content,
              });
            } catch (e) {
              console.error("Embedding update failed:", e);
            }
          },
        },
      );
      setEditPost(null);
    } else {
      createPost.mutate(
        {
          content,
          hashtags,
          mentionedUsers,
          visibility,
          attachmentUrls,
          score: hot(0, new Date()),
          visibleToGroupIds,
          visibleToUserIds,
          groupId,
          authorSnapshot: {
            firstName: user?.firstName ?? "",
            lastName: user?.lastName ?? "",
            email: user?.email ?? "",
            profileImageUrl: user?.profileImageUrl ?? "",
            userId: user?.itemId,
          } as any,
        },
        {
          onSuccess: async (data) => {
            try {
              await embedPost({ post_id: data.insertpost.itemId, content });
            } catch (e) {
              console.error("Embedding failed:", e);
            }
          },
        },
      );
    }
    setModalOpen(false);
  };

  const handleDeletePress = (post: Post) => {
    setDeletePostId(post.ItemId);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!deletePostId) return;
    deletePost.mutate(deletePostId, {
      onSuccess: async (data) => {
        try {
          await deleteEmbeddedPost({ post_id: data.deletepost.itemId });
        } catch (e) {
          console.error("Embedding deletion failed:", e);
        }
      },
    });
    setConfirmDeleteOpen(false);
    setDeletePostId(null);
  };

  const handleCloseViewer = () => {
    _latchedPostId = null;
    setViewerOpen(false);
    setViewerPostId(null);
    setDismissed(true);
    router.setParams({ post_id: undefined });
  };


  const handleCloseReactorModal = () => {
    setReactorModalVisibility(false);
  }
  return {
    displayedPosts,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    search,
    setSearch,
    isSearchLoading,
    handleSearch,
    hasSearched,
    handleLoadMoreSearchResults,
    hasMoreSearchResults,
    modalOpen,
    setModalOpen,
    editPost,
    setEditPost,
    viewerOpen,
    setViewerOpen,
    viewerPostId,
    setViewerPostId,
    handleCloseViewer,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    deletePostId,
    setDeletePostId,
    handleDeleteConfirm,
    handleDeletePress,
    handleSubmit,
    createPost,
    deletePost,

    showReactorModal,
    setReactorModalVisibility,
    handleCloseReactorModal
  };
};
